/**
 * @spell/shared-types
 *
 * Engine-agnostic vocabulary shared by the web client, the authoritative game
 * server and the offline tooling. Nothing in this package may import a browser
 * API, Three.js, React or Colyseus: it has to stay portable so the same spell
 * definitions and combat vocabulary can be re-interpreted by a future
 * Unreal/native runtime.
 */

/* ------------------------------------------------------------------ */
/* Versioning                                                          */
/* ------------------------------------------------------------------ */

/**
 * Bumped whenever the balance table changes in a way that alters numbers.
 *
 * 1.1.0 — volleys: a projectile spell the player described as many small shots
 *         is split into several, each separately parryable, for less total
 *         damage. Purely additive — a spell that is not a volley prices exactly
 *         as it did before.
 */
export const BALANCE_RULES_VERSION = '1.1.0';
/** Bumped whenever the persisted spell JSON shape changes. */
export const SPELL_SCHEMA_VERSION = 1;
/**
 * Bumped whenever the wire protocol changes incompatibly.
 *
 * 2 — `identify` carries the lobby-authored attack spell alongside the parry
 *     spell, and rooms can be opened in practice mode.
 * 3 — the free basic attack is gone; `basicAttack` no longer exists.
 * 4 — a cast no longer names a spell. `cast` carries evidence only, and the
 *     server identifies which spell was performed from the chant and sigil.
 * 5 — a room carries a projectile-speed scale, chosen when it is opened.
 */
export const PROTOCOL_VERSION = 5;

/* ------------------------------------------------------------------ */
/* Spells                                                              */
/* ------------------------------------------------------------------ */

/**
 * `basic` is hand-authored rather than interpreted, and no player owns one — it
 * is the practice range's training bolt and nothing else. Every spell a
 * duellist can cast is `parry` or `signature`, which is to say every one of them
 * was written, priced and registered by the player who casts it.
 */
export const SPELL_TYPES = ['basic', 'parry', 'signature'] as const;
export type SpellType = (typeof SPELL_TYPES)[number];

export const SPELL_CATEGORIES = [
  'attack',
  'defense',
  'heal',
  'trap',
  'mobility',
  'control',
  'buff',
  'debuff',
] as const;
export type SpellCategory = (typeof SPELL_CATEGORIES)[number];

export const SPELL_ELEMENTS = [
  'arcane',
  'fire',
  'frost',
  'shadow',
  'light',
  'storm',
  'nature',
  'blood',
] as const;
export type SpellElement = (typeof SPELL_ELEMENTS)[number];

/**
 * The closed set of executable effect modules. The AI interpreter may only ever
 * emit members of this list; anything else is rejected by schema validation.
 */
export const EFFECT_MODULES = [
  'Projectile',
  'Homing',
  'Explosion',
  'Area',
  'DamageOverTime',
  'Heal',
  'Shield',
  'Reflect',
  'Absorb',
  'StoreAttack',
  'Teleport',
  'Dash',
  'Trap',
  'Stun',
  'Slow',
  'Knockback',
  'BuffNextSpell',
  'RemoveProjectile',
] as const;
export type EffectModule = (typeof EFFECT_MODULES)[number];

/** Presentation-only hints. Never affects damage or balance. */
export const VFX_TAGS = [
  'shards',
  'orb',
  'beam',
  'bolt',
  'swarm',
  'wave',
  'vortex',
  'rune-burst',
  'mirror',
  'chains',
  'embers',
  'crystal',
  'smoke',
  'feathers',
  'starfield',
] as const;
export type VfxTag = (typeof VFX_TAGS)[number];

export const PROJECTILE_SHAPES = ['orb', 'shard', 'beast', 'wave', 'blade', 'swarm'] as const;
export type ProjectileShape = (typeof PROJECTILE_SHAPES)[number];

/* ------------------------------------------------------------------ */
/* Casting                                                             */
/* ------------------------------------------------------------------ */

export const CAST_GRADES = ['Fail', 'Weak', 'Normal', 'Perfect'] as const;
export type CastGrade = (typeof CAST_GRADES)[number];

export const CAST_FAIL_REASONS = [
  'recognition',
  'mana',
  'cooldown',
  'stunned',
  'casting',
  'phase',
  'timestamp',
  'unknown-spell',
  'no-target',
  /** The evidence matched two of the caster's spells too closely to tell apart. */
  'ambiguous',
] as const;
export type CastFailReason = (typeof CAST_FAIL_REASONS)[number];

/* ------------------------------------------------------------------ */
/* Match flow                                                          */
/* ------------------------------------------------------------------ */

export const MATCH_PHASES = [
  'waiting', // waiting for a second player
  'check', // connection / mic / gesture check
  'countdown', // 3..2..1 before a round
  'round', // live combat
  'roundEnd', // scoreboard beat
  'craft', // both players author a signature spell
  'matchEnd', // awards + rematch voting
  'aborted', // unrecoverable disconnect
] as const;
export type MatchPhase = (typeof MATCH_PHASES)[number];

/**
 * What a room is for.
 *
 * A practice room runs the *same* authoritative simulation as a duel — same
 * grading, same parry window, same balance table — with a training dummy in the
 * second stance and no clock. Practising against different rules than the ones
 * the match is decided by would be worse than having no practice mode at all,
 * which is why this is a room mode rather than an offline client simulation.
 */
export const ROOM_MODES = ['duel', 'practice'] as const;
export type RoomMode = (typeof ROOM_MODES)[number];

/* ------------------------------------------------------------------ */
/* Timing / tunables that both sides must agree on                      */
/* ------------------------------------------------------------------ */

export const TIMING = {
  /** Authoritative simulation frequency. */
  TICK_HZ: 30,
  get TICK_MS() {
    return 1000 / TIMING.TICK_HZ;
  },
  /** How often the client pushes aim to the server. */
  AIM_SEND_HZ: 20,

  ROUND_MS: [45_000, 60_000, 75_000] as const,
  CRAFT_MS: 45_000,
  COUNTDOWN_MS: 3_200,
  ROUND_END_MS: 4_000,
  CHECK_MIN_MS: 1_500,

  /** A projectile can be parried this long before its predicted impact. */
  PARRY_WINDOW_MS: 550,
  /** Extra symmetric slack granted to both players to absorb network jitter. */
  PARRY_LATENCY_GRACE_MS: 150,
  /**
   * How long a parryable projectile hangs at the point of impact before it
   * actually resolves.
   *
   * The parry window is judged on speech *onset*, but a cast only reaches the
   * server once the sigil is finished and the key released. Without this hold a
   * defender who began the incantation well inside the window still lost the
   * parry, because the projectile had already hit and despawned by the time the
   * evidence arrived — which also made `PARRY_LATENCY_GRACE_MS` unreachable in
   * practice. The bolt now waits for them, and the wait is the freeze-frame the
   * tutorial is built around.
   */
  PARRY_RESOLVE_HOLD_MS: 220,
  /** Ceiling on the per-defender round-trip added to that hold. */
  PARRY_RESOLVE_RTT_CAP_MS: 300,
  /** The tutorial's first attempt is deliberately forgiving. */
  PARRY_TUTORIAL_GRACE_MS: 450,
  /** How far in the past a client-stamped speech onset may be. */
  MAX_TIMESTAMP_BACKDATE_MS: 900,
  /** How far in the future a client-stamped speech onset may be. */
  MAX_TIMESTAMP_FUTURE_MS: 80,

  /** Grace period before a dropped player forfeits. */
  RECONNECT_GRACE_MS: 20_000,
} as const;

export const COMBAT = {
  MAX_HP: 100,
  MAX_MANA: 100,
  MANA_REGEN_PER_SEC: 9,
  /** Duel stance distance between the two mages, in metres. */
  DUEL_DISTANCE: 14,
  PLAYER_HIT_RADIUS: 0.85,
  /** Aim cone half-angle (radians) inside which a projectile is "on target". */
  AIM_TOLERANCE: 0.14,
  EYE_HEIGHT: 1.65,
} as const;

/**
 * Footwork.
 *
 * A duellist may step now, but a stance is still a stance: movement is bounded
 * to a box around the spot they were seated at, and the box is deliberately not
 * square.
 *
 * Lateral room is generous, because sidestepping is the point. That is the axis
 * a bolt is actually dodged on, and the simulation already has the answer for
 * it — `timeToImpact` reports a shot as no longer closing the moment the
 * defender clears its miss envelope, so clearing it *is* the dodge.
 *
 * Depth is tight, and tighter forward than back, because the distance between
 * the two mages is the unit the parry window is denominated in. The fastest
 * spell the balance table will issue travels 26 m/s, which crosses the full
 * `DUEL_DISTANCE` in 538 ms against a 550 ms window: at full range the window
 * already opens as the shot is fired. Letting the pair close freely would put
 * the window *before* the shot existed and turn parrying into guessing. So a
 * duellist may lean in a little and give ground rather more — and closing is a
 * symmetric bet, since it shortens the approach they have to read too.
 *
 * Casting pins the feet, and that rule lives in the room, not here: the seconds
 * spent standing still saying a chant and drawing a sigil are what every shot
 * in this game costs, and footwork must not be a way to stop paying it.
 */
export const MOVEMENT = {
  /** Walking speed, metres per second. */
  SPEED: 4.2,
  /** Sidestep room either side of the arena axis, in metres. */
  BOX_HALF_X: 4,
  /** How far a duellist may lean toward the opponent, in metres. */
  BOX_FORWARD: 1.5,
  /** How far they may give ground, in metres. */
  BOX_BACK: 3,
  /** Speed multiplier while a Slow is on them. */
  SLOW_SCALE: 0.55,
  /** Heartbeat for re-stating a held direction, in ms. Changes send at once. */
  INPUT_INTERVAL_MS: 200,
  /** Divergence, in metres, past which the client stops easing and snaps. */
  RECONCILE_SNAP_M: 1.5,
  /** How fast a predicted position eases onto the server's, per second. */
  RECONCILE_RATE: 6,
} as const;

/**
 * How fast everything a room fires travels, as a multiple of each spell's own
 * speed.
 *
 * A multiplier rather than an absolute number, because the balance table has
 * already given every spell a speed with meaning in it — a homing shot is slow,
 * that slowness *is* its counterplay window — and replacing those with one flat
 * value would erase the distinction. Scaling keeps the relationships and moves
 * the whole room together.
 *
 * It is a room rule, fixed when the room is opened and published to both
 * duellists, because it changes how much of a shot's flight there is to read.
 * What it deliberately does *not* change is the judgement: the parry window is
 * a fixed span of *time* before impact, so a slower room gives a beginner a
 * longer approach to read without making a parry any more forgiving. Both
 * players are under the same rule, and the server enforces it.
 */
export const PROJECTILE_SPEED_SCALE = { min: 0.4, default: 1, max: 1.6 } as const;

/**
 * Practice-room tunables.
 *
 * The range shares the room speed scale above; only the dummy's cadence is
 * particular to it.
 */
export const PRACTICE = {
  /** Session id of the training dummy. Never a real client. */
  DUMMY_ID: 'training-dummy',
  FIRE_INTERVAL_MS: { min: 600, default: 2600, max: 8000 },
  /** How long a felled duellist (or dummy) lies down before it is restored. */
  REVIVE_DELAY_MS: 1400,
} as const;

/** Score needed to take the match. */
export const ROUNDS_TO_WIN = 2;
export const MAX_ROUNDS = 3;

/* ------------------------------------------------------------------ */
/* Small shared value types                                            */
/* ------------------------------------------------------------------ */

export interface Vec2 {
  x: number;
  y: number;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** A single sampled pointer position, in normalised device-ish space. */
export interface GesturePoint {
  x: number;
  y: number;
  /** Milliseconds since the start of the stroke. */
  t: number;
  /** Stroke index — a gesture may be drawn with several separate strokes. */
  s: number;
}

export type GestureStrokeSample = GesturePoint[];

/* ------------------------------------------------------------------ */
/* Utilities                                                           */
/* ------------------------------------------------------------------ */

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Deterministic, dependency-free id generator (server + tests friendly). */
export function makeId(prefix: string, rng: () => number = Math.random): string {
  let out = '';
  for (let i = 0; i < 8; i++) out += Math.floor(rng() * 36).toString(36);
  return `${prefix}_${out}`;
}

/** Room codes avoid look-alike characters so they can be read out loud. */
const ROOM_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

export function makeRoomCode(rng: () => number = Math.random): string {
  let out = '';
  for (let i = 0; i < 6; i++) {
    out += ROOM_CODE_ALPHABET[Math.floor(rng() * ROOM_CODE_ALPHABET.length)];
  }
  return out;
}

export function isRoomCode(value: string): boolean {
  return /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{6}$/.test(value);
}

/* ------------------------------------------------------------------ */
/* Incantation vocabulary                                              */
/* ------------------------------------------------------------------ */

export * from './lexicon';
